import api from './api';

/**
 * Serviço para gerenciar sessões de chat e conversas
 */
class ChatService {
  
  /**
   * Listar todas as sessões de chat do usuário
   * @param {Object} params - Parâmetros da consulta
   * @param {string} params.status - Status das sessões (active, completed, all)
   * @param {number} params.limit - Limite de sessões a retornar
   * @returns {Promise<Array>} Lista de sessões
   */
  async getSessions(params = {}) {
    try {
      const { status = 'all', limit = 20 } = params;
      console.log('📋 Buscando sessões de chat...', { status, limit });
      
      const response = await api.get('/ai/chatbot/sessions', {
        params: { limit }
      });
      
      console.log('✅ Sessões carregadas:', response.data);
      return response.data.data?.sessions || response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar sessões:', error);
      throw error;
    }
  }

  /**
   * Criar uma nova sessão de chat
   * @param {Object} sessionData - Dados da nova sessão
   * @param {string} sessionData.title - Título da conversa
   * @param {string} sessionData.type - Tipo da conversa (couple, family, personal)
   * @param {string} sessionData.familyId - ID da família (se aplicável)
   * @returns {Promise<Object>} Nova sessão criada
   */
  async createSession(sessionData) {
    try {
      console.log('🆕 Criando nova sessão de chat...', sessionData);
      
      // Inicializar a sessão com uma mensagem inicial
      const initialMessage = this.getInitialMessage(sessionData.type, sessionData.title);
      
      const response = await api.post('/ai/chatbot', {
        message: initialMessage,
        scenario: sessionData.type,
        metadata: {
          title: sessionData.title,
          type: sessionData.type,
          familyId: sessionData.familyId,
          currentFlow: 'initial_assessment'
        }
      });
      
      console.log('✅ Sessão criada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao criar sessão:', error);
      throw error;
    }
  }

  /**
   * Enviar mensagem para uma sessão específica
   * @param {string} sessionId - ID da sessão
   * @param {string} message - Mensagem a enviar
   * @returns {Promise<Object>} Resposta do chat
   */
  async sendMessage(sessionId, message) {
    try {
      console.log('💬 Enviando mensagem...', { sessionId, message });
      
      const response = await api.post('/ai/chatbot', {
        message,
        sessionId
      });
      
      console.log('✅ Resposta recebida:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error);
      throw error;
    }
  }

  /**
   * Finalizar uma sessão de chat
   * @param {string} sessionId - ID da sessão
   * @param {number} satisfaction - Nota de satisfação (1-5)
   * @returns {Promise<Object>} Confirmação de finalização
   */
  async endSession(sessionId, satisfaction = null) {
    try {
      console.log('🔚 Finalizando sessão...', { sessionId, satisfaction });
      
      const response = await api.put(`/ai/sessions/${sessionId}/end`, {
        satisfaction
      });
      
      console.log('✅ Sessão finalizada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao finalizar sessão:', error);
      throw error;
    }
  }

  /**
   * Obter mensagens de uma sessão específica
   * @param {string} sessionId - ID da sessão
   * @returns {Promise<Array>} Lista de mensagens
   */
  async getSessionMessages(sessionId) {
    try {
      console.log('📜 Buscando mensagens da sessão...', sessionId);
      
      const response = await api.get(`/ai/chatbot/sessions/${sessionId}/messages`);
      
      console.log('✅ Mensagens carregadas:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar mensagens:', error);
      throw error;
    }
  }

  /**
   * Conectar uma sessão a um conflito existente
   * @param {string} sessionId - ID da sessão
   * @param {string} conflictId - ID do conflito
   * @returns {Promise<Object>} Confirmação da conexão
   */
  async connectConflict(sessionId, conflictId) {
    try {
      console.log('🔗 Conectando sessão ao conflito...', { sessionId, conflictId });
      
      const response = await api.post('/ai/chatbot/connect-conflict', {
        sessionId,
        conflictId
      });
      
      console.log('✅ Conflito conectado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao conectar conflito:', error);
      throw error;
    }
  }

  /**
   * Gerar mensagem inicial baseada no tipo de conversa
   * @param {string} type - Tipo da conversa
   * @param {string} title - Título da conversa
   * @returns {string} Mensagem inicial
   */
  getInitialMessage(type, title) {
    const messages = {
      'couple': `Olá! Gostaria de conversar sobre "${title}". Como casal, queremos melhorar nossa comunicação e resolver este assunto de forma construtiva.`,
      'family': `Oi! Nossa família quer discutir sobre "${title}". Precisamos de orientação para lidar com esta situação familiar.`,
      'parent_child': `Olá! Como pais, queremos orientação sobre "${title}". Buscamos maneiras de melhorar nossa relação com nossos filhos.`,
      'personal': `Oi! Gostaria de refletir sobre "${title}". Busco autoconhecimento e crescimento pessoal neste assunto.`
    };

    return messages[type] || messages['couple'];
  }

  /**
   * Formatar sessão para exibição no dashboard
   * @param {Object} session - Sessão do backend
   * @returns {Object} Sessão formatada
   */
  formatSessionForDisplay(session) {
    // Priorizar context.title como fonte principal (onde títulos customizados são armazenados)
    const title = session.context?.title || 
                  `Conversa de ${new Date(session.createdAt).toLocaleDateString()}`;
    
    const conversationType = session.context?.conversationType || session.scenario || 'personal';
    const participants = session.context?.participants || [];
    const createdBy = session.context?.createdBy;
    
    // Melhorar lógica para família - verificar familyId e dados do usuário
    let familyName = 'Individual';
    if (session.context?.familyId) {
      familyName = `Família ${session.context.familyId}`;
    } else if (session.context?.familyName) {
      familyName = session.context.familyName;
    } else if (participants.length > 1) {
      familyName = `${participants.length} participantes`;
    } else if (session.userId?.profile?.familyName) {
      familyName = session.userId.profile.familyName;
    }
    
    return {
      id: session.sessionId,
      title: title,
      family: familyName,
      type: this.translateConversationType(conversationType),
      status: session.status === 'active' ? 'Ativa' : 'Finalizada',
      lastActivity: session.lastActivity || session.endedAt || session.createdAt,
      startedAt: session.startedAt || session.createdAt,
      messageCount: session.sessionSummary?.totalMessages || 0,
      participants: participants,
      createdBy: createdBy
    };
  }

  /**
   * Traduzir tipos de conversa
   * @param {string} type - Tipo em inglês
   * @returns {string} Tipo em português
   */
  translateConversationType(type) {
    const translations = {
      'couple': 'Casal',
      'family': 'Família',
      'parent_child': 'Pais e Filhos',
      'personal': 'Pessoal',
      'general': 'Geral',
      'initial': 'Inicial'
    };
    return translations[type] || 'Pessoal';
  }
}

// Exportar instância singleton
const chatService = new ChatService();
export default chatService;